// ===== Staff Management Page (Backend Integrated) =====
import React, { useState } from 'react';
import VenueDashboardHeader from '../components/VenueDashboardHeader';
import { useVenueDashboard } from '../contexts/VenueDashboardContext';
import { PlusIcon, TrashIcon, ShieldCheckIcon, QrCodeIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const ROLE_COLORS = {
  ADMIN: '#dc2626',
  OWNER: '#ea580c',
  MANAGER: '#d97706',
  STAFF: '#16a34a',
  MEMBER: '#6b7280'
};

const StaffManagementPage = () => {
  const { canAccess, staff, loading, refreshData, venueId } = useVenueDashboard();
  const [showInviteModal, setShowInviteModal] = useState(false);

  if (!canAccess(['MANAGER', 'OWNER', 'ADMIN'])) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
        <VenueDashboardHeader title="Staff" />
        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
          <p style={{ color: '#ef4444' }}>Access denied. Manager role or higher required.</p>
        </div>
      </div>
    );
  }

  const formatLastSeen = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const mins = Math.floor(diff / (1000 * 60));
    
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins} mins ago`;
    if (mins < 1440) return `${Math.floor(mins / 60)} hours ago`;
    if (mins < 10080) return `${Math.floor(mins / 1440)} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <VenueDashboardHeader title="Staff & Roles" />
      
      <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
            Team Members ({staff?.length || 0})
          </h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={refreshData}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                backgroundColor: 'white',
                color: '#6b7280',
                fontWeight: '500',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <ArrowPathIcon style={{ width: '16px', height: '16px' }} />
              Refresh
            </button>
            <button
              onClick={() => setShowInviteModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#10b981',
                color: 'white',
                fontWeight: '600',
                fontSize: '14px',
                cursor: 'pointer'
              }}
              data-testid="invite-staff-button"
            >
              <PlusIcon style={{ width: '18px', height: '18px' }} />
              Invite Staff
            </button>
          </div>
        </div>

        {/* Role Permissions Info */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: '0 0 12px 0' }}>
            Role Permissions
          </h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {Object.entries(ROLE_COLORS).map(([role, color]) => (
              <div
                key={role}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  backgroundColor: `${color}15`,
                  borderRadius: '8px'
                }}
              >
                <ShieldCheckIcon style={{ width: '14px', height: '14px', color }} />
                <span style={{ fontSize: '12px', fontWeight: '600', color }}>{role}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <ArrowPathIcon style={{ 
              width: '32px', 
              height: '32px', 
              color: '#10b981',
              animation: 'spin 1s linear infinite'
            }} />
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Empty State */}
        {!loading && (!staff || staff.length === 0) && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '40px',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <ShieldCheckIcon style={{ width: '48px', height: '48px', color: '#9ca3af', margin: '0 auto 16px' }} />
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: '0 0 8px 0' }}>
              No Staff Members Yet
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 20px 0' }}>
              Invite your team to help manage the venue
            </p>
            <button
              onClick={() => setShowInviteModal(true)}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#10b981',
                color: 'white',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Invite Staff
            </button>
          </div>
        )}

        {/* Staff List */}
        {!loading && staff && staff.length > 0 && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            {staff.map((member, index) => {
              const memberRole = member.role || member.staffRole || 'STAFF';
              const roleColor = ROLE_COLORS[memberRole] || ROLE_COLORS.STAFF;
              
              return (
                <div
                  key={member._id || member.id || index}
                  style={{
                    padding: '16px 20px',
                    borderBottom: index < staff.length - 1 ? '1px solid #e5e7eb' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px'
                  }}
                >
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    backgroundColor: roleColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '16px'
                  }}>
                    {(member.user?.name || member.name || member.email || 'U').charAt(0).toUpperCase()}
                  </div>
                  
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                      {member.user?.name || member.name || 'Unknown'}
                    </h3>
                    <p style={{ fontSize: '13px', color: '#6b7280', margin: '2px 0 0 0' }}>
                      {member.user?.email || member.email || 'No email'}
                    </p>
                    <p style={{ fontSize: '12px', color: '#9ca3af', margin: '2px 0 0 0' }}>
                      Last seen: {formatLastSeen(member.lastSeen || member.lastActive || member.updatedAt)}
                    </p>
                  </div>
                  
                  <div style={{
                    padding: '6px 12px',
                    borderRadius: '8px',
                    border: `2px solid ${roleColor}`,
                    backgroundColor: `${roleColor}15`,
                    color: roleColor,
                    fontWeight: '600',
                    fontSize: '12px'
                  }}>
                    {memberRole}
                  </div>
                  
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '500',
                    backgroundColor: member.status === 'active' ? '#dcfce7' : '#fef9c3',
                    color: member.status === 'active' ? '#166534' : '#854d0e'
                  }}>
                    {member.status || 'Active'}
                  </span>
                  
                  <button
                    style={{
                      padding: '8px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      backgroundColor: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <TrashIcon style={{ width: '16px', height: '16px', color: '#ef4444' }} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* QR Onboarding Section */}
        <div style={{
          marginTop: '24px',
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <QrCodeIcon style={{ width: '48px', height: '48px', color: '#6b7280', margin: '0 auto 12px' }} />
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: '0 0 8px 0' }}>
            QR Staff Onboarding
          </h3>
          <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 16px 0' }}>
            Generate a QR code for staff to scan and join your venue
          </p>
          <button
            style={{
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: '#3b82f6',
              color: 'white',
              fontWeight: '600',
              cursor: 'pointer'
            }}
            data-testid="generate-qr-button"
          >
            Generate Onboarding QR
          </button>
        </div>
      </div>
    </div>
  );
};

export default StaffManagementPage;
